(function () {
  function getSampleButtonLabel(button) {
    return button.querySelector('.free-sample-card__button-text') || button;
  }

  function setSampleButtonLabel(button, text) {
    const label = getSampleButtonLabel(button);
    label.textContent = text;
  }

  function readSampleButtonLabel(button) {
    return getSampleButtonLabel(button).textContent.trim();
  }

  class FreeSamples extends HTMLElement {
    constructor() {
      super();

      this.samplesAdded = parseFloat(this.getAttribute('data-samples-added')) || 0;
      this.samplesLimit = parseFloat(this.getAttribute('data-samples-limit')) || 0;
      this.addSamplesImmediately = this.getAttribute('data-add-samples') === 'true';
      this.hitTier = this.getAttribute('data-hit-tier') === 'true';
      this.addButton = this.querySelectorAll('.js-select-sample');
      this.productForm = this.closest('product-info')?.querySelector('.product-form form');
      this.samplesToAdd = [];
      this.pendingButton = null;
      this._sliderProgressBound = false;

      this.addButton.forEach((button) => {
        if (this.addSamplesImmediately && !button.dataset.defaultLabel) {
          button.dataset.defaultLabel = readSampleButtonLabel(button);
        }

        button.addEventListener('click', (event) => {
          event.preventDefault();

          if (button.hasAttribute('disabled')) {
            return;
          }

          const sampleId = button.getAttribute('data-variant-id');

          if (this.addSamplesImmediately) {
            if (this.pendingButton) {
              return;
            }

            if (button.classList.contains('free-sample-card__cta--added')) {
              this.handleRemoveSample(button);
              return;
            }

            if (!this.hitTier || this.samplesAdded >= this.samplesLimit) {
              return;
            }

            this.setButtonLoading(button, true);
            this.markMinicartSampleAdded(button);
            this.handleAddSample(sampleId, button);
          } else {
            if (button.classList.contains('selected')) {
              this.samplesToAdd = this.samplesToAdd.filter((id) => id !== sampleId);
              button.classList.remove('selected');
            } else if (this.samplesToAdd.includes(sampleId)) {
              this.samplesToAdd = this.samplesToAdd.filter((id) => id !== sampleId);
              button.classList.remove('selected');
            } else {
              this.samplesToAdd.push(sampleId);
              button.classList.add('selected');
            }

            this.checkLimit();
          }
        });
      });

      if (!this.addSamplesImmediately) {
        this.productForm?.addEventListener('submit', (event) => {
          event.preventDefault();
          if (this.samplesToAdd.length > 0) {
            this.handleAddSample(this.samplesToAdd.join(','));
            this.samplesToAdd = [];
            this.addButton.forEach((button) => button.classList.remove('selected'));
          }
        });
      }
    }

    connectedCallback() {
      if (this.dataset.sampleKeys && !window.batchCartClearInProgress) {
        this.handleRemoveGift();
      }

      if (this.classList.contains('free-samples--minicart')) {
        this.initMinicartSlider();
      }
    }

    initMinicartSlider() {
      requestAnimationFrame(() => {
        const slider = this.querySelector('slider-component');
        if (typeof slider?.init === 'function') {
          slider.init();
        }
        if (typeof slider?.resetPages === 'function') {
          slider.resetPages();
        }
        this.bindSliderProgress();
      });
    }

    bindSliderProgress() {
      if (this._sliderProgressBound) return;

      const slider = this.querySelector('slider-component');
      const fill = this.querySelector('.free-samples__progress-fill');
      const meter = this.querySelector('.free-samples__progress');
      if (!slider || !fill) return;

      this._sliderProgressBound = true;

      const update = () => {
        const track = slider.slider;
        const total = slider.totalPages || 1;
        let current = 1;

        if (track && total > 1) {
          const maxScroll = track.scrollWidth - track.clientWidth;
          const atEnd =
            slider.nextButton?.hasAttribute('disabled') ||
            (maxScroll > 1 && track.scrollLeft >= maxScroll - 2);

          if (atEnd) {
            current = total;
          } else {
            current = Math.min(total, Math.max(1, slider.currentPage || 1));
          }
        }

        fill.style.width = `${(current / total) * 100}%`;
        if (meter) {
          meter.setAttribute('aria-valuenow', String(current));
          meter.setAttribute('aria-valuemax', String(total));
        }
      };

      slider.addEventListener('slideChanged', update);
      slider.slider?.addEventListener('scroll', update, { passive: true });

      if (slider.slider) {
        new ResizeObserver(() => update()).observe(slider.slider);
      }

      requestAnimationFrame(update);
    }

    checkLimit() {
      if (this.samplesToAdd.length + this.samplesAdded >= this.samplesLimit) {
        this.addButton.forEach((button) => {
          if (!button.classList.contains('selected')) {
            button.setAttribute('disabled', 'true');
          }
        });
      } else {
        this.addButton.forEach((button) => {
          if (!button.classList.contains('selected')) {
            button.removeAttribute('disabled');
          }
        });
      }
    }

    markMinicartSampleAdded(button) {
      button.removeAttribute('disabled');
      button.setAttribute('aria-pressed', 'true');
      button.classList.add('free-sample-card__cta--added');
      button.closest('.free-sample-card')?.classList.add('free-sample-card--added');

      const addedLabel = this.dataset.addedLabel;
      if (addedLabel) {
        setSampleButtonLabel(button, addedLabel);
      }

      this.samplesAdded += 1;
      this.updateSampleCounter();
      this.enforceMinicartLimit();
    }

    revertMinicartSampleAdded(button) {
      button.removeAttribute('aria-pressed');
      button.classList.remove('free-sample-card__cta--added');
      button.closest('.free-sample-card')?.classList.remove('free-sample-card--added');

      if (button.dataset.defaultLabel) {
        setSampleButtonLabel(button, button.dataset.defaultLabel);
      }

      this.samplesAdded = Math.max(0, this.samplesAdded - 1);
      this.updateSampleCounter();
      this.enforceMinicartLimit();
    }

    updateSampleCounter() {
      const counter = this.querySelector('.free-samples__counter');
      if (!counter || !this.samplesLimit) return;

      counter.textContent = `${this.samplesAdded}/${this.samplesLimit}`;
    }

    enforceMinicartLimit() {
      const atLimit = this.samplesAdded >= this.samplesLimit;

      this.addButton.forEach((button) => {
        if (button.classList.contains('free-sample-card__cta--added')) return;

        if (atLimit) {
          button.setAttribute('disabled', 'true');
        } else if (this.hitTier) {
          button.removeAttribute('disabled');
        }
      });
    }

    handleAddSample(ids, revertButton = null) {
      const variantIds = String(ids)
        .split(',')
        .map((s) => Number.parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n));

      if (variantIds.length === 0) return;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: variantIds.map((id) => ({
            id,
            quantity: 1,
            properties: { _sample: 'true' },
          })),
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .then((payload) => {
          const lineKey = payload?.items?.[0]?.key;
          if (revertButton && lineKey) {
            revertButton.dataset.lineKey = lineKey;
          }
          if (revertButton) {
            this.clearLoadingAfterCartRender(revertButton);
          }
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'samples', payload });
        })
        .catch((error) => {
          console.error('Error adding sample:', error);
          if (revertButton) {
            this.revertMinicartSampleAdded(revertButton);
            this.setButtonLoading(revertButton, false);
          }
        });
    }

    setButtonLoading(button, isLoading) {
      this.pendingButton = isLoading ? button : null;
      button.classList.toggle('loading', isLoading);
      button.querySelector('.loading-overlay__spinner')?.classList.toggle('hidden', !isLoading);

      if (isLoading) {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
      }
    }

    // Keep the spinner until the drawer re-renders with the updated cart,
    // so the button can't be toggled against stale markup.
    clearLoadingAfterCartRender(button) {
      let unsubscribe = null;
      let timer = null;

      const finish = () => {
        clearTimeout(timer);
        unsubscribe?.();
        if (this.pendingButton === button) {
          this.setButtonLoading(button, false);
        }
      };

      if (typeof subscribe === 'function') {
        unsubscribe = subscribe(PUB_SUB_EVENTS.renderCartDrawerCachedContent, finish);
      }
      timer = setTimeout(finish, 8000);
    }

    handleRemoveSample(button) {
      const lineKey = button.dataset.lineKey;
      if (!lineKey) return;

      this.setButtonLoading(button, true);
      this.revertMinicartSampleAdded(button);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json();
        })
        .then((payload) => {
          delete button.dataset.lineKey;
          this.clearLoadingAfterCartRender(button);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'samples', payload });
        })
        .catch((error) => {
          console.error('Error removing sample:', error);
          this.markMinicartSampleAdded(button);
          this.setButtonLoading(button, false);
        });
    }

    handleRemoveGift() {
      if (window.batchCartClearInProgress) return;

      const updates = this.dataset.sampleKeys
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean)
        .reduce((acc, key) => {
          acc[key] = 0;
          return acc;
        }, {});

      if (!updates || Object.keys(updates).length === 0) {
        return;
      }

      fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates }),
      })
        .then((response) => response.json())
        .then(() => {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'samples' });
        })
        .catch((error) => console.error('Error removing gift:', error));
    }
  }

  if (!customElements.get('free-samples')) {
    customElements.define('free-samples', FreeSamples);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.renderCartDrawerCachedContent, () => {
      document.querySelectorAll('free-samples.free-samples--minicart').forEach((el) => {
        el._sliderProgressBound = false;
        el.initMinicartSlider?.();
      });
    });
  }
})();
